import { useEffect, useRef, useState } from "react";
import { PageStates } from "../../../services/SystemNames";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import ModalImage from "react-modal-image";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Icons, { CancelIcon, DocumentIcon, OkIcon } from "../../../components/layout/icons/Icons";
import { TextArea } from "@progress/kendo-react-inputs";
import { isNullOrEmpty } from "../../../components/helpers/ObjectHelpers";
import { IncidentReportDialog } from "./IncidentReportDialog";
import { getLoggedInPersonId } from "components/helpers/AuthHelpers";
import { SendToAuthorities } from "./SendToAuthorities";
import { useErrorNotification } from 'components/hooks/useAppStore';

export const PhoneLink = ({ phoneNumber, title }) => {
    return !isNullOrEmpty(phoneNumber) &&
        <div>
            <div className="k-font-bold">{title}</div>
            <div> <a className="phone-link" href={`tel:${phoneNumber}`}>{phoneNumber}</a> </div>
        </div>;
}

export const DialogIconTitle = ({ title, icon }) => {
    return (
        <div className="ob-dialog-title">
            {title}
            <div>
                {icon}
            </div>
        </div>
    );
};

export const FileUploader = ({ handleFile, title, inputType }) => {
    const hiddenFileInput = useRef(null);
    const handleClick = () => {
        hiddenFileInput.current.click();
    };
    const handleChange = event => {
        handleFile(event);
    };
    return (
        <>
            <Button className="ob-image-upload-button" onClick={handleClick}>
                {title}
            </Button>
            <input
                type="file"
                onChange={handleChange}
                id={inputType}
                ref={hiddenFileInput}
                style={{ display: 'none' }}
            />
        </>
    );
}

const SelectOrCaptureImageDialog = ({
    onImageSelected, isImageDialogVisible, onClose
}) => {

    const addErrorNotification = useErrorNotification();

    const handleImageSelected = (e) => {
        onClose();
        const file = e.target.files[0];

        if (file) {
            const reader = new FileReader();
            reader.onloadend = async () => {
                const imageUrl = reader.result;
                const imageType = file.type;
                const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];

                if (allowedFormats.includes(imageType)) {
                    const imageDimensions = await getImageDimensions(imageUrl);
                    onImageSelected(imageUrl, imageDimensions);
                } else {
                    addErrorNotification("Ugyldig bildeformat. Kun JPG, JPEG og PNG er tillatt.");
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const getImageDimensions = async (imageUrl) => {
        const img = new Image();
        img.src = imageUrl;
        await img.decode();
        return { width: img.width, height: img.height };
    };

    return (
        <>
            {isImageDialogVisible &&
                <Dialog className="select-capture-image-dialog" onClose={onClose} title="Velg bilde" >
                    <div>
                        Vil du laste opp et eksisterende eller ta et nytt bilde?
                    </div>
                    <DialogActionsBar>
                        <FileUploader handleFile={handleImageSelected} title={"Last opp"} inputType={"imageInput"} />
                        <FileUploader handleFile={handleImageSelected} title={"Ta et bilde"} inputType={"cameraInput"} />
                    </DialogActionsBar>
                </Dialog>
            }
        </>
    );
};

export const ObservationImage = ({ capturedImage = "", handleImageSelected, isReadOnly = false }) => {
    const [isImageDialogVisible, setIsImageDialogVisible] = useState(false);

    const toggleImageDialog = () => {
        setIsImageDialogVisible(!isReadOnly && !isImageDialogVisible);
    };

    return (
        <div className="ob-image-container">
            {capturedImage && (
                <ModalImage
                    className="ob-image"
                    small={capturedImage}
                    large={capturedImage}
                    alt="Observasjon"
                />
            )}
            {!capturedImage && (
                <Icons
                    className="ob-dummy-image"
                    iconName="dummy"
                    dimensions={128}
                    onClick={toggleImageDialog}
                    alt="Observasjon"
                />
            )}
            <SelectOrCaptureImageDialog
                isImageDialogVisible={!isReadOnly && isImageDialogVisible}
                onImageSelected={ handleImageSelected}
                onClose={toggleImageDialog} />

            {!isReadOnly && <div onClick={toggleImageDialog} className="font-size-lg">
                Klikk her for &aring; ta eller velge et bilde
            </div>}
        </div>
    );
};

export const EditObservation = ({
    isStateChanged,
    isAllowedEdit,
    shipObservationTypes,
    shipObservation,
    onSaveButtonClicked,
    onTypeChanged,
    setState,
    onObservationCommentUpdated,
    observationDate,
    title,
    resetNewObservationState,
    onSelectImage,
    width,
    height,
    handleOnChecked,
}) => {

    const [selectedReportId, setSelectedReportId] = useState(0);
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [isIncidentReportDialogVisible, setIsIncidentReportDialogVisible] = useState(false);

    const isReadOnly = isObservationReadOnly(shipObservation);
    title = isReadOnly ? "Observasjon" : title;
    var hasIncidentReport = !isNullOrEmpty(shipObservation?.incidentFinalReportBinaryContentId);
    var hasPilotIncidentReport = !isNullOrEmpty(shipObservation?.incidentPilotReportBinaryContentId);

    function onCancelButtonClick() {
        if (resetNewObservationState) resetNewObservationState();
        setState(PageStates.Default);
        isStateChanged.current = false;
    }

    const selectedType = shipObservation?.shipObservationType;
    const defaultObservationType = shipObservationTypes[0];

const dropdownData = shipObservationTypes.filter(t => {
    const isAvailableForOwnReporting = t.isAvailableForOwnReporting;
    if (isReadOnly) {
        return t.isVisible === true || t.name === selectedType?.name;
    }
    return (t.isVisible === true && isAvailableForOwnReporting) || t.name === selectedType?.name;
});

    useEffect(() => {
        if (isNullOrEmpty(shipObservation?.shipObservationType)) {
            onTypeChanged(defaultObservationType);
        }
    }, [shipObservation, shipObservationTypes, defaultObservationType, onTypeChanged]);

    const handleOnTypeChanged = (event) => {
        onTypeChanged(event.target.value);
    }

    const onFinalReportButtonClicked = () => {
        showIncidentReportDialog("final");
        setSelectedReportId(shipObservation.incidentFinalReportBinaryContentId);
    }

    const onPilotReportButtonClicked = () => {
        showIncidentReportDialog("pilot");
        setSelectedReportId(shipObservation.incidentPilotReportBinaryContentId);
    }

    const showIncidentReportDialog = (reportType) => {
        setSelectedReportType(reportType)
        setIsIncidentReportDialogVisible(true);
    }

    const onIncidentReportDialogClosed = () => {
        setSelectedReportType(null);
        setIsIncidentReportDialogVisible(false);
        setSelectedReportId(0);
    }

    function isObservationReadOnly(shipObservation) {
        return isObservationTypeReadOnly(shipObservation?.shipObservationType) || !isLoggedInPersonCreaterOfObservation(shipObservation?.modifiedByPerson);
    }

    function isObservationTypeReadOnly(type) {
        return type && !type.isVisible;
    }

    function isLoggedInPersonCreaterOfObservation(observationCreatorPerson) {
        if(isNullOrEmpty(observationCreatorPerson)) return true;
        return observationCreatorPerson.modifiedByPersonId === getLoggedInPersonId();
    }

    return (
        <Dialog
            onClose={onCancelButtonClick}
            width={width}
            height={height}
            title={title}>
            <div className="ob-add-container">
                <ObservationImage
                    isReadOnly={isReadOnly}
                    handleImageSelected={onSelectImage}
                    capturedImage={shipObservation?.imageBase64Url}
                />
                <div className="vertical-bar-container">
                    <div className="vertical-bar" style={{ backgroundColor: shipObservation?.shipObservationType?.color ?? defaultObservationType.color }}>
                        <DropDownList
                            className="ob-type-dropdown-list font-size-lg"
                            adaptive={true}
                            fillMode={null}
                            textField={"name"}
                            onChange={handleOnTypeChanged}
                            adaptiveTitle={"Velg type"}
                            value={dropdownData.find(t => t.name === selectedType?.name) ?? defaultObservationType}
                            data={dropdownData}
                            disabled={isReadOnly}
                        />
                    </div>
                </div>
                <TextArea
                    disabled={!isAllowedEdit}
                    readOnly={isReadOnly}
                    placeholder="Beskriv observasjon..."
                    className="ob-add-textarea font-size-lg"
                    onChange={onObservationCommentUpdated}
                    value={shipObservation?.observation ?? ""} />
                <div>{observationDate}</div>
                <SendToAuthorities
                    isReadOnly={isReadOnly}
                    hasBeenPreviousSendt={shipObservation?.adjacentPilotShipReport}
                    isStateChanged={isStateChanged}
                    handleCheckedChange={handleOnChecked}
                />

            </div>
            {(hasIncidentReport || hasPilotIncidentReport) &&
                <DialogActionsBar>
                    {
                        hasIncidentReport &&
                        <Button
                            disabled={!hasIncidentReport}
                            onClick={onFinalReportButtonClicked}
                            svgIcon={DocumentIcon}>
                            Sluttrapport
                        </Button>
                    }
                    {
                        hasPilotIncidentReport &&
                        <Button
                            disabled={!hasPilotIncidentReport}
                            onClick={onPilotReportButtonClicked}
                            svgIcon={DocumentIcon}>
                            Losens rapport
                        </Button>
                    }
                </DialogActionsBar>}

            <DialogActionsBar>
                <Button
                    onClick={onCancelButtonClick}
                    svgIcon={CancelIcon}>
                    Avbryt
                </Button>
               {!isReadOnly && <Button
                    disabled={(!isAllowedEdit || !isStateChanged.current || isReadOnly)}
                    onClick={onSaveButtonClicked}
                    themeColor="primary"
                    svgIcon={OkIcon}>
                    Lagre
                </Button>}
            </DialogActionsBar>
            {
                isIncidentReportDialogVisible &&
                <IncidentReportDialog
                    onClose={onIncidentReportDialogClosed}
                    selectedReportType={selectedReportType}
                    id={selectedReportId}>
                </IncidentReportDialog>
            }
        </Dialog>
    );
};
